import { useEffect, useRef } from "react";
import { atom, useAtomValue } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import trackEtaEstimates from "common/utils/delivery-date/track-eta-estimates";
import { useCompositionForm } from "./useCompositionForm";
import { useCurrentComposition } from "./useCurrentComposition";

const DELIVERY_SYNC_DEBOUNCE_MS = 3000;

/**
 * Atom incremented every time a quantity changes in the Design Lab.
 * SizeSelector.changeQuantity bumps this counter.
 */
export const quantityChangedAtom = atom(0);

/**
 * Build a variants hash from all addedCompositions.
 */
function buildVariantsHash(
  addedCompositions: Array<{ id: string; catalogId: string; variants?: Record<string, number> }>
): { hash: Record<string, string>; hasVariants: boolean } {
  const hash: Record<string, string> = {};
  let hasVariants = false;

  addedCompositions.forEach(comp => {
    if (!comp.variants) return;
    Object.entries(comp.variants).forEach(([variantId, quantity]) => {
      if (quantity > 0) {
        const existing = hash[variantId] ? parseInt(hash[variantId], 10) : 0;
        hash[variantId] = String(existing + quantity);
        hasVariants = true;
      }
    });
  });

  return { hash, hasVariants };
}

/**
 * Triggers a delivery estimate refetch on every quantity change (debounced).
 *
 * - **On page load** (edit design): fetches immediately if quantities exist.
 * - **On every quantity change**: debounced fetch with ALL variants from ALL
 *   compositions in a single API call.
 * - Does NOT trigger on color/product switches — only on actual quantity changes.
 */
export function useDeliveryDateSync(): void {
  const { form } = useCompositionForm();
  const { currentComposition } = useCurrentComposition();
  const queryClient = useQueryClient();

  const quantityVersion = useAtomValue(quantityChangedAtom);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFetchingRef = useRef(false);
  const pendingFetchRef = useRef<(() => void) | null>(null);
  const hasInitialFetchedRef = useRef(false);
  const prevCompositionIdRef = useRef<string | null>(null);

  useEffect(() => {
    const compositionId = currentComposition?.id ?? form.getValues("currentCompositionId");
    const compositionIdStr = compositionId != null ? String(compositionId) : null;
    const compositionChanged =
      compositionIdStr != null &&
      prevCompositionIdRef.current != null &&
      compositionIdStr !== prevCompositionIdRef.current;

    // After initial load, only react to quantity changes or composition switches.
    // Delivery dates are per-composition, so a color switch must trigger a refetch.
    if (quantityVersion === 0 && hasInitialFetchedRef.current && !compositionChanged) {
      return undefined;
    }

    if (!compositionId) return undefined;

    // Clear stale delivery data from the previous composition so the UI
    // doesn't show the wrong date while the new fetch is in-flight.
    if (compositionChanged) {
      queryClient.setQueryData(["deliveryEstimates"], undefined);
    }

    const addedCompositions = form.getValues("addedCompositions");
    let { hash: variantsHash, hasVariants } = buildVariantsHash(addedCompositions);

    // On initial load with no quantities, default to 1 unit of the first
    // variant so the header always shows a delivery date.
    if (!hasVariants && !hasInitialFetchedRef.current) {
      const firstVariant = currentComposition?.variants?.[0];
      if (firstVariant) {
        variantsHash = { [String(firstVariant.id)]: "1" };
        hasVariants = true;
      }
    }

    if (!hasVariants) {
      prevCompositionIdRef.current = compositionIdStr;
      return undefined;
    }

    const isFirstFetchWithVariants = !hasInitialFetchedRef.current;

    const doFetch = () => {
      if (isFetchingRef.current) {
        pendingFetchRef.current = doFetch;
        return;
      }
      pendingFetchRef.current = null;
      isFetchingRef.current = true;
      hasInitialFetchedRef.current = true;
      prevCompositionIdRef.current = compositionIdStr;

      const useFastEta = window?.jiffy?.flip?.fastDeliveryEstimate === true;
      const variantsParam = encodeURIComponent(JSON.stringify(variantsHash));

      const url = useFastEta
        ? `/api/eta/print.json?variants_hash=${variantsParam}&source=pdp`
        : `/api/made/current_pdp_date?composition_id=${compositionId}&variants_hash=${variantsParam}`;

      fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      })
        .then(r => r.json())
        .then(raw => {
          if (useFastEta) trackEtaEstimates(raw, { cap: true });
          return raw;
        })
        .then(objNamesToCamelCase)
        .then(res => {
          if (useFastEta && res?.success && res?.estimates) {
            const allEstimates = Object.values(
              res.estimates as Record<string, Record<string, unknown>>
            ).filter(Boolean);
            const estimate = allEstimates.reduce<Record<string, unknown> | undefined>(
              (latest, curr) => {
                if (!latest) return curr;
                const latestDate = String(latest["deliveryDate"] ?? "");
                const currDate = String(curr["deliveryDate"] ?? "");
                return currDate > latestDate ? curr : latest;
              },
              undefined
            );
            if (estimate) {
              const d = estimate["date"];
              queryClient.setQueryData(["deliveryEstimates"], {
                valueFull: d,
                timeleft: estimate["timeleft"] || null,
                deliveryType: null,
                deliveryTime: estimate["deliveryTime"] || null,
                deliveryTimeEnd: estimate["deliveryTimeEnd"] || null,
                shipments: [{ date: d, dateWithDayName: d, items: 1 }],
                onlyCartShipments: [{ date: d, dateWithDayName: d, items: 1 }],
                shippingCostInfo: null
              });
            }
          } else if (res?.success && res.compositionDates) {
            const dates = res.compositionDates;
            if (dates.valueFull && dates.valueFull.includes("&")) {
              dates.valueFull = dates.valueFull.split("&")[0].trim();
            }
            queryClient.setQueryData(["deliveryEstimates"], dates);
          }
        })
        .catch(() => {
          // Silently fail
        })
        .finally(() => {
          isFetchingRef.current = false;
          const pending = pendingFetchRef.current;
          if (pending) {
            pendingFetchRef.current = null;
            pending();
          }
        });
    };

    if (isFirstFetchWithVariants) {
      doFetch();
      return undefined;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(doFetch, DELIVERY_SYNC_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      pendingFetchRef.current = null;
    };
  }, [quantityVersion, currentComposition, form, queryClient]);
}
